import { navigate, useRoute } from "../lib/router";
import { prefetchRoute } from "../lib/routePrefetch";
import { useNotifications } from "../lib/notifications";
import { FishIcon, MailIcon, MapIcon, PinIcon, UserIcon } from "../ui";

// Lagoon-language Nav — spartan wordmark on the caustic gradient (§6.1
// header) and a bottom TabBar on mobile / a matching top tab row on ≥sm.
// Account is a tab in the primary nav rather than a header dropdown, so
// every top-level destination lives in the same place. Sign-out, admin
// console, and the account email now live inside AccountPage.

const TABS: {
  href: string;
  label: string;
  Icon: (p: { className?: string }) => JSX.Element;
}[] = [
  { href: "/plan", label: "Plan", Icon: MapIcon },
  { href: "/", label: "Spot", Icon: PinIcon },
  { href: "/collections", label: "Collection", Icon: FishIcon },
  { href: "/feedback", label: "Feedback", Icon: MailIcon },
  { href: "/account", label: "Account", Icon: UserIcon },
];

function isActive(route: string, href: string): boolean {
  if (href === "/") return route === "/";
  return route === href || route.startsWith(href + "/");
}

export function Nav() {
  const route = useRoute();
  const { total: pendingCount } = useNotifications();

  const badgeFor = (href: string) =>
    href === "/account" && pendingCount > 0 ? pendingCount : 0;

  const bottomTab = (href: string, label: string, Icon: typeof PinIcon) => {
    const on = isActive(route, href);
    const badge = badgeFor(href);
    return (
      <button
        key={href}
        type="button"
        onClick={() => navigate(href)}
        // Warm the route's JS chunk on pointerdown so the actual click
        // races against a fetch that's already in flight. Typically saves
        // 80–120 ms on the pointerdown → click gap.
        onPointerDown={() => prefetchRoute(href)}
        aria-current={on ? "page" : undefined}
        aria-label={badge > 0 ? `${label} (${badge} pending)` : undefined}
        className={`flex flex-1 flex-col items-center gap-1 rounded-lg py-1.5 text-[11px] leading-tight font-semibold tracking-[0.03em] transition
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep
          ${on ? "text-aqua-deep" : "text-meta hover:text-ink"}`}
      >
        <span className="relative inline-flex">
          <Icon className="h-[22px] w-[22px]" />
          {badge > 0 && (
            <span
              aria-hidden
              className="absolute -right-1.5 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-coral px-1 text-[9px] font-bold text-white"
            >
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </span>
        <span>{label}</span>
      </button>
    );
  };

  const topTab = (href: string, label: string, Icon: typeof PinIcon) => {
    const on = isActive(route, href);
    const badge = badgeFor(href);
    return (
      <button
        key={href}
        type="button"
        onClick={() => navigate(href)}
        onPointerDown={() => prefetchRoute(href)}
        aria-current={on ? "page" : undefined}
        aria-label={badge > 0 ? `${label} (${badge} pending)` : undefined}
        className={`inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep focus-visible:ring-offset-2
          ${
            on
              ? "bg-aqua-deep text-paper shadow-card"
              : "text-meta hover:bg-aqua/10 hover:text-ink"
          }`}
      >
        <span className="relative inline-flex">
          <Icon className="h-4 w-4" />
          {badge > 0 && (
            <span
              aria-hidden
              className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-coral px-1 text-[9px] font-bold text-white"
            >
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </span>
        {label}
      </button>
    );
  };

  return (
    <>
      <header className="sticky top-0 z-20 bg-paper/90 backdrop-blur-md">
        <div className="relative overflow-hidden bg-grad-header pt-[env(safe-area-inset-top)] text-paper">
          <div className="g-rays" aria-hidden />
          <div className="g-caustics" aria-hidden />
          <div className="relative mx-auto flex max-w-3xl items-center justify-between">
            <a
              href={`https://github.com/akash-test-fun/fish-finder/commit/${__APP_COMMIT__}`}
              target="_blank"
              rel="noreferrer"
              title="Deployed commit"
              className="absolute right-4 top-1 z-10 font-mono text-[10px] leading-none text-paper/60 hover:text-paper"
            >
              #{__APP_COMMIT__}
            </a>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="flex min-w-0 items-center gap-3 px-4 pb-3 pt-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <span className="min-w-0">
                <span className="block truncate font-display text-2xl font-semibold leading-tight text-paper">
                  Reefscope
                </span>
                <span className="block truncate text-xs font-medium text-paper/75">
                  See it. Click it. Save it.
                </span>
              </span>
            </button>
          </div>
        </div>
        <div className="hidden border-b border-hairline sm:block">
          <div className="mx-auto flex max-w-3xl items-center gap-1 px-4 py-2">
            {TABS.map((t) => topTab(t.href, t.label, t.Icon))}
          </div>
        </div>
      </header>

      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-20 flex items-stretch gap-1 border-t border-ring bg-paper/95 px-2 pt-2 pb-[calc(0.35rem+env(safe-area-inset-bottom))] backdrop-blur-md sm:hidden"
      >
        {TABS.map((t) => bottomTab(t.href, t.label, t.Icon))}
      </nav>
    </>
  );
}
